import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ordersAPI, productsAPI } from '../utils/api';
import { useCart } from '../context/CartContext';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { ru, enUS, et, ka, hy, az, it, hi } from 'date-fns/locale';
import { FaEye, FaRedo } from 'react-icons/fa';
import '../styles/Orders.css';

const Orders = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { t, i18n } = useTranslation();
  
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [repeating, setRepeating] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const getDateLocale = () => {
    const locales = { ru, en: enUS, et, ka, hy, az, it, hi };
    return locales[i18n.language] || et;
  };

  useEffect(() => {
    if (location.state?.message) {
      setSuccessMessage(location.state.message);
      // Очистить сообщение из состояния
      window.history.replaceState({}, document.title);
    }
    loadOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  const loadOrders = async () => {
    setLoading(true);
    try {
      const response = await ordersAPI.getOrders();
      setOrders(response.data.data);
    } catch (error) {
      console.error('Ошибка загрузки заказов:', error);
      setError(t('errorLoadingOrders', 'Error loading orders'));
    } finally {
      setLoading(false);
    }
  };

  const handleRepeatOrder = async (orderId) => {
    setRepeating(true);
    try {
      // Получаем данные о заказе
      const orderResponse = await ordersAPI.getOrder(orderId);
      const order = orderResponse.data.data;
      
      if (!order || !order.items || order.items.length === 0) {
        alert(t('orderHasNoItems', 'Заказ не содержит товаров'));
        setRepeating(false);
        return;
      }
      
      // Загружаем актуальные данные о товарах и добавляем в корзину
      const addedItems = [];
      const unavailableItems = [];
      
      for (const item of order.items) {
        try {
          if (!item.product) {
            unavailableItems.push(item.name || t('unknownProduct', 'Неизвестный товар'));
            continue;
          }
          const productResponse = await productsAPI.getProduct(item.product._id);
          const product = productResponse.data.data;
          
          // Проверяем только активность товара, остатки не проверяем
          if (!product.isActive) {
            unavailableItems.push(item.name);
            continue;
          }
          
          // Добавляем товар в корзину с тем же количеством, что и в заказе
          addToCart(product, item.quantity, item.unit);
          addedItems.push(item.name);
          
        } catch (err) {
          console.error(`Ошибка загрузки товара ${item.name}:`, err);
          unavailableItems.push(item.name);
        }
      }
      
      // Формируем сообщение для пользователя
      let message = '';
      if (addedItems.length > 0) {
        message += `✅ ${t('addedToCart', 'Добавлено в корзину')}: ${addedItems.length} ${t('items', 'позиций')}\n`;
      }
      if (unavailableItems.length > 0) {
        message += `\n❌ ${t('unavailable', 'Недоступно')}:\n${unavailableItems.join(', ')}`;
      }
      
      if (addedItems.length > 0) {
        alert(message);
        navigate('/cart');
      } else {
        alert(t('allItemsUnavailable', 'К сожалению, все товары из заказа сейчас недоступны'));
      }
      
    } catch (error) {
      console.error('Ошибка повторения заказа:', error);
      alert(t('repeatOrderFailed', 'Не удалось повторить заказ'));
    } finally {
      setRepeating(false);
    }
  };

  const getStatusLabel = (status) => {
    const statusKey = status === 'completed' ? 'delivered' : status;
    return t(statusKey, status);
  };

  const getStatusClass = (status) => {
    const classes = {
      pending: 'status-pending',
      confirmed: 'status-confirmed',
      processing: 'status-processing',
      completed: 'status-completed',
      cancelled: 'status-cancelled',
      rejected: 'status-rejected'
    };
    return classes[status] || '';
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>{t('loading', 'Loading orders...')}</p>
      </div>
    );
  }

  return (
    <div className="orders-page">
      <div className="orders-container">
        <h1>{t('myOrders')}</h1>

        {successMessage && (
          <div className="success-message">
            {successMessage}
          </div>
        )}

        {error && (
          <div className="error-message">{error}</div>
        )}

        {orders.length === 0 ? (
          <div className="no-orders">
            <p>{t('noOrders', 'You have no orders yet')}</p>
            <button onClick={() => navigate('/catalog')} className="btn-primary">
              {t('goToCatalog')}
            </button>
          </div>
        ) : (
          <div className="orders-list">
            {orders.map((order) => (
              <div key={order._id} className="order-card">
                <div className="order-header">
                  <div className="order-number">
                    <strong>{t('orderNumber')} #{order.orderNumber}</strong>
                  </div>
                  <div className={`order-status ${getStatusClass(order.status)}`}>
                    {getStatusLabel(order.status)}
                  </div>
                </div>

                <div className="order-body">
                  <div className="order-info">
                    <div className="order-date">
                      <strong>{t('createdDate', 'Created date')}:</strong>{' '}
                      {format(new Date(order.createdAt), 'd MMMM yyyy, HH:mm', { locale: getDateLocale() })}
                    </div>
                    <div className="order-delivery">
                      <strong>{t('deliveryDate')}:</strong>{' '}
                      {format(new Date(order.deliveryDate), 'd MMMM yyyy', { locale: getDateLocale() })}
                    </div>
                    <div className="order-items-count">
                      <strong>{t('products', 'Products')}:</strong> {order.items.length}
                    </div>
                  </div>

                  <div className="order-total">
                    <strong>{t('total')}</strong> {order.total.toFixed(2)} €
                  </div>
                </div>

                <div className="order-actions">
                  <button
                    onClick={() => navigate(`/orders/${order._id}`)}
                    className="btn-view"
                  >
                    <FaEye /> {t('viewDetails')}
                  </button>
                  {(order.status === 'completed' || order.status === 'cancelled') && (
                    <button
                      onClick={() => handleRepeatOrder(order._id)}
                      className="btn-repeat"
                      disabled={repeating}
                      style={{ opacity: repeating ? 0.6 : 1 }}
                    >
                      <FaRedo style={{ animation: repeating ? 'spin 1s linear infinite' : 'none' }} /> 
                      {repeating ? t('loading') : t('repeatOrder', 'Repeat order')}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Orders;

